/**
 * /parity/uo_activation — 线网同构：`admin/uo.Activation/index`（激活码转 TP/EP/RP）
 * 只读池与 uo.uoc 同源（php_uo_uo_config 镜像）；见 admin-parity-uo-activation.routes.ts
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, ConfigProvider, Form, Input, Modal, Select, theme } from 'antd';
import type { CSSProperties } from 'react';
import { useAuthStore } from '../../store/auth';
import { api, ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { TOKEN_META } from '@uo/shared';

type LegacySnap = {
  uoc: string;
  rp: string;
  tp: string;
  ep: string;
  mirror_imported_at: string | null;
  has_mirror: boolean;
};

type LogRow = {
  id: string;
  user_id: number;
  asset: string;
  delta: string;
  note: string | null;
  created_at: string;
};

const poolInputStyle: CSSProperties = {
  marginLeft: 8,
  flex: 1,
  maxWidth: 560,
  backgroundColor: '#9F9F9F',
  color: '#fff',
  fontWeight: 500,
  border: '1px solid #8c8c8c',
};

const readRowBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  marginBottom: 12,
  fontSize: 14,
};

const formItemMb = 18;

export function ParityUoActivationPage() {
  const { message: msg } = App.useApp();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const hasMenu = useAuthStore((s) => s.hasMenu);
  const isSuper = useAuthStore((s) => s.profile?.is_super === true);
  const canUse =
    isSuper ||
    hasMenu('parity.uo_activation') ||
    hasNode('parity.uo_activation') ||
    hasNode('platform.user.transfer_coin');

  const { data: snap, isLoading, refetch: refetchSnap } = useQuery({
    queryKey: ['parity-uo-activation', 'legacy-php-form'],
    queryFn: () => api.get<LegacySnap>('/api/admin/parity-uo-activation/legacy-php-form'),
    enabled: canUse,
  });

  const [form] = Form.useForm<{
    account: string;
    num: string;
    coin: 'rp' | 'tp' | 'ep';
    remark?: string;
  }>();
  const { token } = theme.useToken();

  const mut = useMutation({
    mutationFn: (v: {
      account: string;
      num: string;
      coin: 'rp' | 'tp' | 'ep';
      remark?: string;
      xian: boolean;
    }) => api.post<unknown>('/api/admin/parity-uo-activation/distribute', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
      void refetchSnap();
      msg.success('发放成功');
      form.resetFields(['num', 'account', 'remark']);
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '失败'),
  });

  const onSubmit = () => {
    form
      .validateFields()
      .then((v) => {
        Modal.confirm({
          title: '确定操作吗？',
          onOk: () => {
            mut.mutate({
              account: v.account,
              num: String(v.num).trim(),
              coin: v.coin,
              remark: v.remark?.trim() || undefined,
              xian: false,
            });
          },
        });
      })
      .catch(() => {});
  };

  const logColumns = [
    { title: '金额', dataIndex: 'delta', width: 120 },
    { title: '备注', dataIndex: 'note', width: 300, ellipsis: true },
    {
      title: '资产',
      dataIndex: 'asset',
      width: 72,
      render: (v: string) => (TOKEN_META as Record<string, { symbol: string }>)[v]?.symbol ?? v,
    },
    {
      title: '发放时间',
      dataIndex: 'created_at',
      width: 180,
      render: (t: string) => new Date(t).toLocaleString('zh-CN'),
    },
  ];

  if (!canUse) {
    return <p style={{ color: '#999' }}>无权限：需要 parity.uo_activation 或 platform.user.transfer_coin</p>;
  }

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#1677ff' } }}>
      <div className="parity-uo-activation-page" style={{ background: '#fff', minHeight: 360, padding: '0 0 8px' }}>
        <div style={{ maxWidth: 900 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'rgba(0,0,0,0.88)',
              marginBottom: 14,
            }}
          >
            激活码转 TP/EP/RP
          </div>
          <p style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 12, lineHeight: 1.6 }}>
            对齐线网 <code>uo.Activation</code>：只读 UOC/RP/TP/EP 池 + 发放数量、币种、收款登录名。后端写入 ledger（
            <code>parity-uo-activation/distribute</code>）。
          </p>
          {snap && !snap.has_mirror && !isLoading && (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                background: token.colorInfoBg,
                border: `1px solid ${token.colorBorderSecondary}`,
                fontSize: 12,
              }}
            >
              无 php_uo_uo_config 镜像时池子显示为 0。导入镜像后刷新页面可同步与线网一致的只读数。
            </div>
          )}
          {isLoading ? null : (
            <>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right', color: 'rgba(0,0,0,0.85)' }}>UOC剩余</span>
                <Input readOnly value={snap?.uoc ?? ''} size="large" style={poolInputStyle} />
              </div>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right' }}>RP</span>
                <Input readOnly value={snap?.rp ?? ''} size="large" style={poolInputStyle} />
              </div>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right' }}>TP</span>
                <Input readOnly value={snap?.tp ?? ''} size="large" style={poolInputStyle} />
              </div>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right' }}>EP</span>
                <Input readOnly value={snap?.ep ?? ''} size="large" style={poolInputStyle} />
              </div>
            </>
          )}
          <Form
            form={form}
            layout="horizontal"
            labelAlign="right"
            colon={false}
            requiredMark
            style={{ maxWidth: 900 }}
            initialValues={{ coin: 'rp' as const }}
            labelCol={{ flex: '0 0 200px' }}
            wrapperCol={{ flex: '1 1 auto' }}
          >
            <Form.Item
              name="num"
              label="发放数量"
              rules={[{ required: true, message: '请输入发放数量' }]}
              style={{ marginBottom: formItemMb }}
            >
              <Input size="large" placeholder="发放数量" allowClear style={{ maxWidth: '100%' }} />
            </Form.Item>
            <Form.Item
              name="coin"
              label="转换币种"
              rules={[{ required: true, message: '请选择转换币种' }]}
              style={{ marginBottom: formItemMb }}
            >
              <Select
                size="large"
                style={{ maxWidth: '100%' }}
                placeholder="请选择"
                options={[
                  { value: 'rp' as const, label: 'RP' },
                  { value: 'tp' as const, label: 'TP' },
                  { value: 'ep' as const, label: 'EP' },
                ]}
              />
            </Form.Item>
            <Form.Item name="remark" label="备注" style={{ marginBottom: formItemMb }}>
              <Input.TextArea rows={2} placeholder="可选，记入流水备注" style={{ maxWidth: '100%' }} />
            </Form.Item>
            <Form.Item
              name="account"
              label="用户登录名"
              rules={[{ required: true, message: '请输入登录名' }]}
              style={{ marginBottom: 22 }}
            >
              <Input size="large" placeholder="登录名" autoComplete="off" style={{ maxWidth: '100%' }} />
            </Form.Item>
            <div style={{ position: 'relative', minHeight: 44, marginTop: 12 }}>
              <Button
                type="primary"
                size="small"
                style={{ position: 'absolute', left: 0, bottom: 0 }}
                onClick={() => {
                  void refetchSnap();
                  void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
                  msg.info('已刷新数据');
                }}
              >
                刷新页面
              </Button>
              <div style={{ display: 'flex', justifyContent: 'center', paddingLeft: 100 }}>
                <Button
                  type="primary"
                  size="large"
                  loading={mut.isPending}
                  onClick={onSubmit}
                  style={{ minWidth: 140, height: 40 }}
                >
                  发放
                </Button>
              </div>
            </div>
          </Form>
        </div>

        <div
          style={{
            marginTop: 28,
            paddingTop: 20,
            borderTop: '1px solid #e8e8e8',
            maxWidth: 900,
          }}
        >
          <div style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'rgba(0,0,0,0.88)' }}>转币记录</div>
          <DataTable<LogRow>
            queryKey={['admin-transfer-coin-log', 'parity-activation']}
            endpoint="/api/admin/transfer-coin/log"
            columns={logColumns}
            rowKey="id"
          />
        </div>
      </div>
    </ConfigProvider>
  );
}
